<template>
  <div class="vuse-content-wrapper">
    <vuse-section-definition
        :title="sectionNameText"
        :companyName="companyNameText"
        icon="mdi-account-supervisor"/>
    <v-container fluid class="fill-width">
      <v-sheet class="neu-glow with-radius">
        <v-data-table
            :loading="getLoader"
            :headers="filteredHeaders"
            :items="users"
            :single-expand="true"
            :expanded.sync="expanded"
            item-key="userLogin"
            sort-by="userLogin"
            show-expand
            class="elevation-1 crud-table expanded-crud-table px-6 neu-glow"
        >
          <template v-slot:top>
            <v-toolbar flat color="transparent" class="pt-2">
              <v-spacer/>
              <v-btn color="primary" v-if="isRolSuper || isRolSiteAdmin" dark class="mb-2" @click="newUser">{{ $t('Global.Users.newUser') }}</v-btn>
              <users-dialog
                  v-if="dialogActive"
                  :activate="dialogActive"
                  :item="editedItem"
                  :isViewSuper="isViewSuper"
                  @create-item="createItem"
                  @update-item="updateItem"
                  @close-dialog="dialogActive=false"/>
            </v-toolbar>
          </template>

          <template #[`expanded-item`]="{ headers, item }">
            <td :colspan="headers.length" class="neu-glow-inset">
              <users-expansion :item="item" :headers="headers" />
            </td>
          </template>

          <template #[`item.roleId`]="{ item }">
            <span>{{ getRoleType(item) }}</span>
          </template>

          <template #[`item.actions`]="{ item }">
            <v-tooltip content-class='custom-tooltip' left>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                    color="primary"
                    size="20"
                    class="mr-3"
                    v-bind="attrs"
                    v-on="on"
                    delay-open="200"
                    :open-on-click="false"
                    :open-on-focus="false"
                    @click="editItem(item)"
                >mdi-pencil
                </v-icon>
              </template>
              <span>{{ $t('Global.Users.edit') }}</span>
            </v-tooltip>
            <v-tooltip content-class='custom-tooltip' bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                    :color="!!item.locked ? 'gray' : 'primary'"
                    size="20"
                    class="mr-3"
                    v-bind="attrs"
                    v-on="on"
                    delay-open="200"
                    :open-on-click="false"
                    :open-on-focus="false"
                    @click="lockHandler(item, !item.locked )"
                >{{ !!item.locked ? 'mdi-account-cancel' : 'mdi-account' }}
                </v-icon>
              </template>
              <span>{{ !!item.locked ? $t('Global.Users.toUnblock') : $t('Global.Users.toBlock') }}</span>
            </v-tooltip>
            <v-tooltip content-class='custom-tooltip' right>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                    color="primary"
                    size="20"
                    class="mr-3"
                    v-bind="attrs"
                    v-on="on"
                    delay-open="200"
                    :open-on-click="false"
                    :open-on-focus="false"
                    @click="deleteItem(item)"
                >mdi-delete
                </v-icon>
              </template>
              <span>{{ $t('Global.Users.delete') }}</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-sheet>
    </v-container>
    <confirm-dialog
        ref="confirmDialog"
        :title="confirmDialogTitle"
        :message="confirmDialogMessage"
    />
    <v-snackbar
        v-model="snackbar"
        absolute
        top
        right
        :timeout="snackbarTimeout"
        :color="snackbarColor"
        class="mt-6"
    >
      <span>{{ snackbarMessage }}</span>
      <v-icon class="ml-2" dark>{{ snackbarIcon }}</v-icon>
      <template v-slot:action="{ attrs }">
        <v-btn
            color="white"
            text
            v-bind="attrs"
            @click="snackbar = false"
        >
          {{ $t("Global.ConfirmDialog.buttonTextClose") }}
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import UserApi from "@/mixins/Services/UserApi";
import snackBar from "@/mixins/snackBarMixin";
import loader from "@/mixins/Loader";
import Utils from "@/mixins/Utils";
import Logout from "@/mixins/Logout";
import ConfirmDialog from "@/components/UI/Dialogs/ConfirmDialog";
import UsersDialog from './UsersDialog.vue'
import UsersExpansion from './UsersExpansion.vue'
import {companiesMock} from "@/infrastructure/MocksResponses";
import {mapGetters} from 'vuex'
import ROL from "@/config/rol-values";

export default {
  name: "Users",
  components: {
    UsersDialog,
    UsersExpansion,
    ConfirmDialog
  },
  mixins: [UserApi, snackBar, loader, Utils, Logout],
  data() {
    return {
      users: [],
      expanded: [],
      headers: [
        {text: this.$t('Global.Users.userLogin'), value: "userLogin", align: "left", sortable: true},
        {text: this.$t('Global.Users.firstName'), value: "firstName", align: "left", sortable: true},
        {text: this.$t('Global.Users.email'), value: "email", align: "left", sortable: false},
        {text: this.$t('Global.Users.role'), value: "roleId", align: "left", sortable: true},
        {text: this.$t('Global.Users.actions'), value: "actions", align: "center", sortable: false, width: "130px"},
        {text: '', value: 'data-table-expand'}
      ],
      editedItem: null,
      dialogActive: false,
      confirmDialogTitle: "",
      confirmDialogMessage: null,
      companiesList: companiesMock,
      rol: ROL,
      isViewSuper: false
    }
  },
  computed: {
    ...mapGetters(['getCompanyName','getEditCompanyInfoName', "getRoleId", "getDefsRolesTypes", "getUserId"]),
    filteredHeaders() {
      if (this.headers.length) {
         return this.isRolSuper || this.isRolSiteAdmin
             ? this.headers
             : this.headers.filter(h => h.value !== "actions")

      }
      else return []
    },
    isRolSuper(){
      return [this.rol.SUPER_ADMIN, this.rol.SUPER_USER].includes(this.getRoleId)
    },
    isRolSiteAdmin(){
      return [this.rol.SITE_ADMIN].includes(this.getRoleId)
    },
    sectionNameText(){
      return this.isViewSuper
          ? this.$t('Global.Main.superUsersManagement')
          : this.$t('Global.Main.usersManagement')
    },
    companyNameText(){
      return this.isViewSuper
          ? ''
          : this.getEditCompanyInfoName || this.getCompanyName
    }
  },
  watch: {
    '$route.meta.viewSuperUser'() {
      this.isViewSuper = this.$route.meta.viewSuperUser
      this.getUsersList()
    },
    deep: true,
    immediate: true
  },
  methods: {
    lockHandler(item, value) {
      const toUpdateItem = Object.assign({}, item)
      toUpdateItem.locked = Number(value)
      this.updateItem(toUpdateItem, this.$t("Global.Users.blockUser"))
    },
    getRoleType(item) {
      return this.getDefsRolesTypes?.find((e) => e.roleId === item.roleId)?.roleName
    },
    getCompany(item) {
      return this.companiesList.find((e) => e.companyId === item.companyId)?.companyName
    },
    async getUsersList() {
      try {
        this.setLoader(true)
        this.users = await (
            this.isRolSuper && this.isViewSuper
            ? this.getUsers('super').then((r) => r.filter((f)=> [this.rol.SUPER_ADMIN, this.rol.SUPER_USER].includes(f.roleId) ))
            : this.getUsersByCompanyId().then((r) => r.filter((f)=> [this.rol.SITE_ADMIN, this.rol.SITE_USER, this.rol.DISPLAY_USER].includes(f.roleId))) )
        this.setLoader(false)
      } catch (e) {
        this.users = []
        console.error('[USERS][getUsersList] ERROR:', e)
        this.setLoader(false)
        if(this.isErrorText(e)){
          const errorCode = this.getErrorText(e);
          this.$emit('launch-snackbar', this.$t(`Global.Error.c${errorCode}`), 'error', "4000", 'mdi-alert-octagon')
        }
      }
    },
    newUser() {
      this.dialogActive = true
      this.editedItem = null
    },
    editItem(item) {
      this.dialogActive = true
      this.editedItem = item
    },
    async createItem(item) {
      this.confirmDialogTitle = this.$t("Global.Users.newUser")
      const confirm = await this.$refs.confirmDialog.open()
      if (!confirm) return
      try{
        this.setLoader(true)
        this.isRolSuper ? await this.createSuperUser(item) : await this.createUser(item)
        this.setLoader(false)
        this.launchSnackbar(this.$t("Global.Users.userCreatedOk"), "success", "4000", "mdi-check-circle")
        await this.getUsersList()
      } catch (e) {
        console.error('[USERS][createUser] ERROR:', e)
        this.setLoader(false)
        if(this.isErrorText(e)){
          const errorCode = this.getErrorText(e);
          this.$emit('launch-snackbar', this.$t(`Global.Error.c${errorCode}`), 'error', "4000", 'mdi-alert-octagon')
        }
      }
    },
    async updateItem(item, messageTitle = this.$t("Global.Users.editUser")) {
      this.confirmDialogTitle = messageTitle
      const confirm = await this.$refs.confirmDialog.open()
      if (!confirm) return
      try {
        this.setLoader(true)
        this.isRolSuper ? await this.updateSuperUser(item) : await this.updateUser(item)
        this.setLoader(false)
        this.launchSnackbar(this.$t("Global.Users.userEditedOk"), "success", "4000", "mdi-check-circle")
        await this.getUsersList()
      } catch (e) {
        console.error('[USERS][updateUser] ERROR:', e)
        this.setLoader(false)
        if(this.isErrorText(e)){
          const errorCode = this.getErrorText(e);
          this.$emit('launch-snackbar', this.$t(`Global.Error.c${errorCode}`), 'error', "4000", 'mdi-alert-octagon')
        }
      }

    },
    async deleteItem(item) {
      this.confirmDialogTitle = this.$t("Global.Users.deleteUser")
      const confirm = await this.$refs.confirmDialog.open()
      if (!confirm) return
      try {
        this.setLoader(true)
        this.isRolSuper ? await this.deleteSuperUser(item.userId) : await this.deleteUser(item.userId)
        this.setLoader(false)
        this.launchSnackbar(this.$t("Global.Users.userDeletedOk"), "success", "4000", "mdi-check-circle")
        await this.getUsersList()
      } catch (e) {
        console.error('[USERS][deleteUser] ERROR:', e)
        this.setLoader(false)
        if(this.isErrorText(e)){
          const errorCode = this.getErrorText(e);
          this.$emit('launch-snackbar', this.$t(`Global.Error.c${errorCode}`), 'error', "4000", 'mdi-alert-octagon')
        }
      }
    }
  },
  mounted() {
    this.isViewSuper = this.$route.meta.viewSuperUser
    this.getUsersList()
  }
}
</script>
